import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../config/supabase';
import toast from 'react-hot-toast';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: ${props => props.theme.colors.background};
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 12px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const LoadingText = styled.p`
  color: ${props => props.theme.colors.text.primary};
  font-size: 1.2rem;
  margin-top: 20px;
`;

const ErrorText = styled.p`
  color: ${props => props.theme.colors.error};
  font-size: 1.2rem;
  margin-top: 20px;
  text-align: center;
  max-width: 400px;
`;

const SuccessText = styled.p`
  color: ${props => props.theme.colors.success || '#4CAF50'};
  font-size: 1.2rem;
  margin-top: 20px;
  text-align: center;
  max-width: 400px;
`;

const RetryButton = styled.button`
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  margin-top: 20px;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover {
    background: ${props => props.theme.colors.accentHover};
  }
`;

const LoadingSpinner = styled.div`
  border: 4px solid ${props => props.theme.colors.surface};
  border-top: 4px solid ${props => props.theme.colors.accent};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 20px auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('processing');

  const handleCallback = useCallback(async () => {
    try {
      console.log('🔄 Processing auth callback...');
      console.log('📍 Current location:', location.pathname + location.search + location.hash);
      
      // Handle both hash and query parameters
      const hashParams = new URLSearchParams(location.hash.substring(1));
      const searchParams = new URLSearchParams(location.search);
      
      // PKCE (modern) flow
      const code = searchParams.get('code') || hashParams.get('code');
      // Legacy flow
      const token = searchParams.get('token') || hashParams.get('token');
      const type = searchParams.get('type') || hashParams.get('type');
      
      console.log('🔍 Auth parameters found:', { code: !!code, token: !!token, type });
      
      // Check for errors in hash
      const error = hashParams.get('error');
      const errorDescription = hashParams.get('error_description');
      
      if (error) {
        console.error('Auth error:', error, errorDescription);
        setStatus('error');
        
        // Handle specific error cases
        switch (error) {
          case 'server_error':
            setError('Server error occurred. Please try again or contact support if the problem persists.');
            break;
          case 'invalid_request':
            setError('Invalid request. Please try registering or logging in again.');
            break;
          case 'unauthorized_client':
            setError('Authentication failed. Please try again.');
            break;
          default:
            setError(`Authentication error: ${errorDescription || error}`);
        }
        return;
      }
      
      if (code) {
        // PKCE flow
        console.log('🔄 Processing PKCE flow with code...');
        console.log('📝 Code length:', code.length);
        
        try {
          // Add timeout to prevent hanging
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Request timeout - please try again')), 15000);
          });
          
          const exchangePromise = supabase.auth.exchangeCodeForSession(code);
          
          const { data, error: exchangeError } = await Promise.race([exchangePromise, timeoutPromise]);
          console.log('📊 Exchange result:', { hasData: !!data, hasError: !!exchangeError });
          
          if (exchangeError) {
            console.error('Error exchanging code for session:', exchangeError);
            setStatus('error');
            switch (exchangeError.message) {
              case 'Invalid code':
                setError('Invalid or expired verification link. Please request a new verification email.');
                break;
              case 'Email not confirmed':
                setError('Please verify your email address first.');
                break;
              default:
                setError(`Failed to complete authentication: ${exchangeError.message}`);
            }
            return;
          }
          
          if (data?.user) {
            console.log('✅ PKCE authentication successful:', data.user.email);
            console.log('📊 User data:', {
              id: data.user.id,
              email: data.user.email,
              emailConfirmed: !!data.user.email_confirmed_at,
              createdAt: data.user.created_at
            });
            console.log('📊 Session data:', {
              accessToken: !!data.session?.access_token,
              refreshToken: !!data.session?.refresh_token,
              expiresAt: data.session?.expires_at
            });
            
            // Give AuthService a moment to pick up the new session
            await new Promise(resolve => setTimeout(resolve, 500));
            
            setStatus('success');
            toast.success('Email verified successfully! You can now sign in to SceneFeed!');
            
            // Redirect to sign-in page after a short delay
            setTimeout(() => {
              console.log('🔄 Redirecting to sign-in page...');
              navigate('/login');
            }, 1000);
          } else {
            console.log('❌ No user data in response:', data);
            setStatus('error');
            setError('No user data received. Please try again.');
          }
        } catch (exchangeErr) {
          console.error('❌ Exception during code exchange:', exchangeErr);
          setStatus('error');
          if (exchangeErr.message.includes('timeout')) {
            setError(`Verification is taking longer than expected. This might be due to slow internet connection. Please try refreshing the page or use the retry button below.`);
          } else {
            setError(`Exchange error: ${exchangeErr.message}`);
          }
        }
        return;
      }

      if (token && type) {
        // Legacy flow
        console.log('🔄 Processing legacy flow with token...');
        try {
          // Add timeout to prevent hanging
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Request timeout - please try again')), 15000);
          });
          
          const verifyPromise = supabase.auth.verifyOtp({ token, type });
          
          const { data, error: verifyError } = await Promise.race([verifyPromise, timeoutPromise]);
          console.log('📊 Verify result:', { hasData: !!data, hasError: !!verifyError });
          
          if (verifyError) {
            console.error('Error verifying OTP:', verifyError);
            setStatus('error');
            switch (verifyError.message) {
              case 'Invalid token':
                setError('Invalid or expired verification link. Please request a new verification email.');
                break;
              case 'Email not confirmed':
                setError('Please verify your email address first.');
                break;
              default:
                setError(`Failed to complete authentication: ${verifyError.message}`);
            }
            return;
          }
          
          if (data?.user) {
            console.log('✅ Legacy authentication successful:', data.user.email);
            setStatus('success');
            toast.success('Email verified successfully! You can now sign in to SceneFeed!');
            
            // Redirect to sign-in page after a short delay
            setTimeout(() => {
              navigate('/login');
            }, 1000);
          } else {
            console.log('❌ No user data in response:', data);
            setStatus('error');
            setError('No user data received. Please try again.');
          }
        } catch (verifyErr) {
          console.error('❌ Exception during OTP verification:', verifyErr);
          setStatus('error');
          if (verifyErr.message.includes('timeout')) {
            setError(`Verification is taking longer than expected. This might be due to slow internet connection. Please try refreshing the page or use the retry button below.`);
          } else {
            setError(`Verification error: ${verifyErr.message}`);
          }
        }
        return;
      }

      // No authentication parameters found
      console.log('⚠️ No authentication parameters found');
      setStatus('error');
      setError('No authentication code or token provided. Please try the verification link again.');
    } catch (err) {
      console.error('❌ Error in auth callback:', err);
      setStatus('error');
      setError(`An unexpected error occurred: ${err.message}`);
    }
  }, [location, navigate]);

  useEffect(() => {
    handleCallback();
  }, [handleCallback]);

  const handleRetry = () => {
    setError(null);
    setStatus('processing');
    handleCallback();
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (status === 'error') {
    return (
      <Container>
        <Card>
          <ErrorText>{error}</ErrorText>
          <RetryButton onClick={handleRetry}>
            Try Again
          </RetryButton>
          <RetryButton onClick={handleGoToLogin} style={{ marginLeft: '10px', background: '#666' }}>
            Go to Sign In
          </RetryButton>
        </Card>
      </Container>
    );
  }

  if (status === 'success') {
    return (
      <Container>
        <Card>
          <SuccessText>Email verified successfully!</SuccessText>
          <LoadingText>Redirecting you to sign in...</LoadingText>
          <LoadingSpinner />
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <LoadingText>Verifying your email...</LoadingText>
        <LoadingSpinner />
      </Card>
    </Container>
  );
};

export default AuthCallback; 